const isIdOnlyLabel = (value: string): boolean => {
  const text = value.trim()
  if (!text) return true
  return (
    /^id\s*[:#-]?\s*\d+$/i.test(text) ||
    /^part\s*#?\s*\d+$/i.test(text) ||
    /^sub\s*component\s*#?\s*\d+$/i.test(text) ||
    /^subcomponent\s*#?\s*\d+$/i.test(text) ||
    /^vessel\s*#?\s*\d+$/i.test(text)
  )
}

const cleanValue = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined
  const text = String(value).trim()
  if (!text || text === '-') return undefined
  return isIdOnlyLabel(text) ? undefined : text
}

export const uniqueJoinedValues = (values: Array<string | null | undefined>): string => {
  const seen = new Set<string>()
  const result: string[] = []
  values.forEach((value) => {
    const text = cleanValue(value)
    if (!text) return
    const key = text.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    result.push(text)
  })
  return result.length > 0 ? result.join(', ') : '-'
}

export const isTechnicalRequest = (requestFor?: unknown): boolean => {
  const text = cleanValue(requestFor)
  return text ? text.toUpperCase() === 'TECHNICAL' : false
}

export type RequisitionViewContext = 'TECHNICAL' | 'GENERAL_STORES' | 'SERVICES'

export const resolveRequisitionViewContext = (
  requestFor?: unknown,
  categoryName?: unknown
): RequisitionViewContext => {
  if (isTechnicalRequest(requestFor)) return 'TECHNICAL'
  const category = cleanValue(categoryName)?.toUpperCase() || ''
  if (category.includes('SERVICE')) return 'SERVICES'
  return 'GENERAL_STORES'
}

export const getContextHeaderLabels = (
  context: RequisitionViewContext
): {primary: string; secondary: string} => {
  if (context === 'TECHNICAL') {
    return {primary: 'Equipment', secondary: 'Subcomponent'}
  }
  if (context === 'SERVICES') {
    return {primary: 'Head', secondary: 'Sub Head'}
  }
  return {primary: 'Category', secondary: 'Sub Category'}
}

export const getEquipmentNameFromLine = (line: any): string | undefined => {
  return (
    cleanValue(line?.equipmentName) ||
    cleanValue(line?.equipment?.name) ||
    cleanValue(line?.subcomponent?.equipmentName) ||
    cleanValue(line?.subcomponent?.equipment?.name) ||
    cleanValue(line?.part?.subcomponent?.equipmentName) ||
    cleanValue(line?.part?.subcomponent?.equipment?.name)
  )
}

export const getSubcomponentNameFromLine = (line: any): string | undefined => {
  return (
    cleanValue(line?.subcomponentName) ||
    cleanValue(line?.subcomponent?.name) ||
    cleanValue(line?.part?.subcomponent?.name)
  )
}

export const getSubcomponentMakerFromLine = (line: any): string | undefined => {
  return (
    cleanValue(line?.subcomponentMaker) ||
    cleanValue(line?.subcomponent?.maker) ||
    cleanValue(line?.part?.subcomponent?.maker)
  )
}

export const getEquipmentMakerFromLine = (line: any): string | undefined =>
  cleanValue(line?.equipmentMaker) || cleanValue(line?.equipment?.maker) || cleanValue(line?.subcomponent?.equipment?.maker)

export const getEquipmentModelFromLine = (line: any): string | undefined =>
  cleanValue(line?.equipmentModel) || cleanValue(line?.equipment?.model) || cleanValue(line?.subcomponent?.equipment?.model)

export const getSubcomponentModelFromLine = (line: any): string | undefined =>
  cleanValue(line?.subcomponentModel) || cleanValue(line?.subcomponent?.model) || cleanValue(line?.part?.subcomponent?.model)

export const getItemDisplayNameFromLine = (line: any, idx = 0): string => {
  return (
    cleanValue(line?.manualItemName) ||
    cleanValue(line?.lineItemName) ||
    cleanValue(line?.part?.name) ||
    cleanValue(line?.partName) ||
    cleanValue(line?.itemName) ||
    cleanValue(line?.itemDescription) ||
    cleanValue(line?.subcomponent?.name) ||
    cleanValue(line?.subcomponentName) ||
    `Item ${idx + 1}`
  )
}

export const getPartNameFromLine = (line: any): string | undefined => {
  return (
    cleanValue(line?.part?.name) ||
    cleanValue(line?.partName) ||
    cleanValue(line?.manualItemName) ||
    cleanValue(line?.lineItemName) ||
    cleanValue(line?.itemName)
  )
}

export const getTechnicalContextLabel = (
  line: any,
  requestFor?: unknown
): string | undefined => {
  if (!isTechnicalRequest(requestFor)) return undefined
  const equipment = getEquipmentNameFromLine(line)
  const subcomponent = getSubcomponentNameFromLine(line)
  if (equipment && subcomponent) return `${equipment} / ${subcomponent}`
  return equipment || subcomponent
}

export const getVesselNameFromEntity = (entity: any): string | undefined => {
  return (
    cleanValue(entity?.vessel?.fleet_name) ||
    cleanValue(entity?.vessel?.fleetName) ||
    cleanValue(entity?.vessel?.name) ||
    cleanValue(entity?.vessel?.vesselName) ||
    cleanValue(entity?.vesselName) ||
    cleanValue(entity?.category?.vesselName) ||
    cleanValue(entity?.rfq?.requisition?.vessel?.fleet_name) ||
    cleanValue(entity?.rfq?.requisition?.vessel?.fleetName) ||
    cleanValue(entity?.rfq?.requisition?.vessel?.name) ||
    cleanValue(entity?.rfq?.requisition?.vessel?.vesselName) ||
    cleanValue(entity?.rfq?.requisition?.vesselName)
  )
}

export const getCompanyNameFromEntity = (entity: any): string | undefined => {
  return (
    cleanValue(entity?.companyName) ||
    cleanValue(entity?.company?.name) ||
    cleanValue(entity?.category?.cgaid?.name) ||
    cleanValue(entity?.rfq?.requisition?.category?.cgaid?.name) ||
    cleanValue(entity?.rfq?.requisition?.companyName)
  )
}

export const getLinkedDefectInfo = (
  entity: any
): {id?: number; number?: string; title?: string} => {
  const idRaw = entity?.linkedDefectId
  const id =
    typeof idRaw === 'number'
      ? idRaw
      : typeof idRaw === 'string' && idRaw.trim() !== '' && !Number.isNaN(Number(idRaw))
      ? Number(idRaw)
      : undefined
  const number = cleanValue(entity?.linkedDefectNumber)
  const title = cleanValue(entity?.linkedDefectTitle)
  return {id, number, title}
}

export const getLinkedDefectDisplay = (entity: any): string | undefined => {
  const info = getLinkedDefectInfo(entity)
  const primary =
    info.number && info.id != null
      ? `${info.number} (ID:${info.id})`
      : info.number || (info.id != null ? `ID:${info.id}` : undefined)
  if (!primary && !info.title) return undefined
  if (!info.title) return primary
  if (!primary) return info.title
  return `${primary} - ${info.title}`
}
